import React, { useEffect, useRef, useState } from "react";
import { Upload, Trash2, FileSpreadsheet, Loader2, RefreshCw, ImagePlus, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import { fileUrl } from "@/lib/api";

export default function AdminSolitaires() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [usdRate, setUsdRate] = useState({ rate: 83, manual: null, source: "manual" });
  const [manualInput, setManualInput] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const excelRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/solitaires?limit=300${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Failed to load solitaires");
    } finally { setLoading(false); }
  };

  const loadUsd = async () => {
    try {
      const { data } = await api.get("/solitaires/usd-rate");
      setUsdRate({ rate: data.rate, manual: data.manual_override, source: data.source });
      if (data.manual_override) setManualInput(String(data.manual_override));
    } catch (err) {
      console.error("[AdminSolitaires] USD rate fetch failed:", err);
    }
  };

  useEffect(() => { load(); loadUsd(); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const handleExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadProgress({ pct: 0, label: "Uploading…" });
    const tId = toast.loading("Uploading Excel…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/solitaires/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded * 100) / evt.total);
          setUploadProgress({ pct, label: `Uploading — ${pct}%` });
        },
      });
      toast.success(`Inserted ${data.inserted}, Updated ${data.updated} (${data.total} total)`, { id: tId, duration: 7000 });
      await load();
    } catch (err) {
      toast.error(`Upload failed: ${formatError(err?.response?.data?.detail) || err?.message}`, { id: tId });
    } finally {
      setUploadProgress(null);
      e.target.value = "";
    }
  };

  const saveUsdRate = async () => {
    const val = manualInput ? parseFloat(manualInput) : 0;
    try {
      await api.post("/admin/solitaires/usd-rate", { usd_rate_manual: val > 0 ? val : null });
      toast.success(val > 0 ? `USD rate set to ₹${val.toFixed(2)}/$` : "Manual override cleared (using API rate)");
      await loadUsd();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Failed to update");
    }
  };

  const refreshUsd = async () => {
    try { await api.post("/admin/solitaires/usd-rate", { usd_rate_manual: null }); await loadUsd(); toast.success("Refreshed from API"); }
    catch (err) {
      console.error("[AdminSolitaires] refresh USD failed:", err);
      toast.error("Failed to refresh USD rate");
    }
  };

  const remove = async (stock_id) => {
    if (!window.confirm(`Delete solitaire ${stock_id}?`)) return;
    try { await api.delete(`/admin/solitaires/${stock_id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(formatError(err?.response?.data?.detail) || "Failed"); }
  };

  const uploadCert = async (stock_id, file) => {
    const tId = toast.loading(`Uploading certificate for ${stock_id}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/solitaires/${stock_id}/cert-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificate uploaded", { id: tId });
      await load();
    } catch (err) { toast.error(formatError(err?.response?.data?.detail) || "Upload failed", { id: tId }); }
  };

  return (
    <div className="p-8 space-y-6 max-w-[1280px]">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-ivory">Solitaires</h1>
          <p className="text-ivory/60 text-sm mt-1">{total} certified diamonds · Excel-driven inventory</p>
        </div>
        <div className="flex items-center gap-3">
          <input ref={excelRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcel} data-testid="solitaire-excel-input" />
          <button onClick={() => excelRef.current?.click()} className="inline-flex items-center gap-2 bg-rose-gold text-ink-900 px-4 py-3 text-sm font-medium hover:bg-champagne transition-colors" data-testid="solitaire-excel-upload">
            <FileSpreadsheet className="w-4 h-4" /> Bulk upload Excel
          </button>
        </div>
      </div>

      {uploadProgress && (
        <div className="border border-rose-gold/40 bg-ink-800/60 px-4 py-3 flex items-center gap-4">
          <div className="flex-1">
            <div className="text-sm text-ivory mb-1.5">{uploadProgress.label}</div>
            <div className="w-full h-1.5 bg-white/10"><div className="h-full bg-rose-gold transition-all" style={{ width: `${uploadProgress.pct}%` }} /></div>
          </div>
          <div className="text-rose-gold font-display text-xl tabular-nums">{uploadProgress.pct}%</div>
        </div>
      )}

      {/* USD rate */}
      <div className="border border-rose-gold/30 bg-ink-800/60 p-5">
        <div className="overline text-rose-gold mb-3">USD → INR Rate (live)</div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-[11px] tracking-widest uppercase text-ivory/60 mb-1.5">Current</label>
            <div className="font-display text-3xl text-ivory">₹{Number(usdRate.rate).toFixed(2)}<span className="text-base text-ivory/50 ml-1">/$</span></div>
            <div className="text-[10px] text-ivory/40 mt-1 uppercase tracking-widest">Source · {usdRate.source}</div>
          </div>
          <div className="flex items-end gap-2 flex-1 min-w-[280px]">
            <div className="flex-1">
              <label className="block text-[11px] tracking-widest uppercase text-ivory/60 mb-1.5">Manual override (₹/$)</label>
              <input type="number" step="0.01" value={manualInput} onChange={(e) => setManualInput(e.target.value)} placeholder="leave blank to use API" className="w-full px-3 py-2.5 bg-ink-900 border border-white/10 text-ivory focus:border-rose-gold" data-testid="usd-manual-input" />
            </div>
            <button onClick={saveUsdRate} className="px-4 py-2.5 bg-rose-gold/15 border border-rose-gold/50 text-rose-gold text-sm tracking-widest uppercase hover:bg-rose-gold/25" data-testid="usd-manual-save">Save</button>
            <button onClick={refreshUsd} className="px-4 py-2.5 border border-white/10 text-ivory/70 hover:text-rose-gold" title="Clear manual and refresh from API" data-testid="usd-refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Search + table */}
      <input
        type="text"
        placeholder="Search by stock ID or cert no…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full md:w-96 px-4 py-2.5 bg-ink-900 border border-white/10 text-ivory focus:border-rose-gold"
        data-testid="solitaire-admin-search"
      />

      <div className="border border-white/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-800/70 text-ivory/60">
            <tr>
              <Th>Stock ID</Th><Th>Shape</Th><Th>Carat</Th><Th>Colour</Th><Th>Clarity</Th>
              <Th>Cert No</Th><Th>Lab</Th><Th>RAP</Th><Th>DISC</Th><Th>360</Th><Th>Cert img</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={12} className="text-center py-10 text-ivory/40"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></td></tr>
            )}
            {!loading && items.map((it) => (
              <tr key={it.stock_id} className="border-t border-white/5 hover:bg-rose-gold/5">
                <Td><span className="font-mono text-rose-gold">{it.stock_id}</span></Td>
                <Td>{it.shape || "—"}</Td>
                <Td>{it.carat ? `${it.carat.toFixed(2)}` : "—"}</Td>
                <Td>{it.colour || "—"}</Td>
                <Td>{it.clarity || "—"}</Td>
                <Td>{it.cert_no || "—"}</Td>
                <Td>{it.cert_lab || "—"}</Td>
                <Td>{it.rap || "—"}</Td>
                <Td>{it.disc_pct != null ? `${it.disc_pct}%` : "—"}</Td>
                <Td>{it.video_link ? <a href={it.video_link} target="_blank" rel="noreferrer" className="text-rose-gold hover:text-champagne inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> view</a> : "—"}</Td>
                <Td>
                  {it.cert_image_url ? (
                    <a href={fileUrl(it.cert_image_url)} target="_blank" rel="noreferrer" className="text-rose-gold hover:text-champagne text-xs">view</a>
                  ) : (
                    <CertUpload onUpload={(f) => uploadCert(it.stock_id, f)} />
                  )}
                </Td>
                <Td>
                  <button onClick={() => remove(it.stock_id)} className="text-ivory/40 hover:text-hallmark" data-testid={`delete-solitaire-${it.stock_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-3 py-3 text-[11px] tracking-widest uppercase">{children}</th>;
const Td = ({ children }) => <td className="px-3 py-2.5 text-ivory/80 align-middle">{children}</td>;

const CertUpload = ({ onUpload }) => {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
      <button onClick={() => ref.current?.click()} className="text-xs inline-flex items-center gap-1 text-ivory/50 hover:text-rose-gold">
        <ImagePlus className="w-3.5 h-3.5" /> upload
      </button>
    </>
  );
};
